<template>
  <div v-if="permissions.includes('document.read')">
    <div class="my-4 mx-3">
      <div
        class="d-flex flex-md-row flex-column gap-2 justify-content-md-between justify-content-center align-items-center">
        <div>
          <h3 class="text-dark">Documents</h3>
          <ol class="breadcrumb m-0">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'DashboardContent' }" tag="a">
                Dashboard
              </router-link>
            </li>
            <li class="breadcrumb-item active">Documents</li>
          </ol>
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-primary btn-sm my-md-0 my-3" @click="openModal">
            Add Document
          </button>
          <button class="btn btn-info btn-sm my-md-0 my-3" @click="openFieldsManager">
            Manage Fields
          </button>
        </div>
      </div>


      <DocumentTable :rows="rows" :totalRecords="totalRecords" :columns="columns" :perpage_list="perpage_list"
        @delete="deleteDocument" @edit="goToDocument" @download="openDownloadModal" />
    </div>

    <AddDocument ref="docModal" @save="saveDocument" />
    <DocumentFieldsManager ref="fieldsModal" />
    <div class="modal fade" ref="downloadModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Download {{ selectedDocument ? selectedDocument.name : '' }}
            </h5>
            <button class="btn-close" @click="closeDownloadModal"></button>
          </div>

          <div class="modal-body">
            <!-- Template Selection -->
            <div class="mb-3">
              <label class="form-label">Template </label>
              <multiselect v-model="selectedTemplate" :options="templates" label="name" track-by="id"
                placeholder="Select Template" :multiple="false" :close-on-select="true" :clear-on-select="false"
                :preserve-search="true" @input="onTemplateChange" />
            </div>

            <!-- Custom Details Template: Select Fields -->
            <div v-if="selectedTemplate && selectedTemplate.template_type === 'custom_details'" class="mb-3">
              <label class="form-label">Custom Details</label>
              <div class="card p-3" style="max-height: 400px; overflow-y: auto;">
                <div class="row g-3">
                  <div v-for="field in templateFields" :key="field.name" class="col-12 col-md-6">
                    <label class="form-label form-label-sm text-break w-100">{{ field.label }}  <span class="text-danger">*</span></label>
                    <input 
                      v-if="field.field_type === 'text'"
                      type="text"
                      v-model="customDetail[field.name]"
                      class="form-control form-control-sm"
                      :placeholder="field.placeholder"
                      required
                    />
                    <div v-else-if="field.field_type === 'email'" class="mb-2">
                      <input 
                        type="email"
                        v-model="customDetail[field.name]"
                        class="form-control form-control-sm"
                        placeholder="example@domain.com"
                        required
                      />
                   
                    </div>
                    <input 
                      v-else-if="field.field_type === 'date'"
                      type="date"
                      v-model="customDetail[field.name]"
                      class="form-control form-control-sm"
                      required
                    />
                    <input 
                      v-else-if="field.field_type === 'number'"
                      type="number"
                      v-model="customDetail[field.name]"
                      class="form-control form-control-sm"
                      required
                    />
                    <input 
                      v-else-if="field.field_type === 'amount'"
                      type="text"
                      v-model="customDetail[field.name]"
                      class="form-control form-control-sm"
                      placeholder="0.00"
                      pattern="[0-9.]*"
                      inputmode="decimal"
                      @input="validateAmountInput(field.name)"
                      required
                    />
                    <textarea 
                      v-else-if="field.field_type === 'textarea'"
                      v-model="customDetail[field.name]"
                      class="form-control form-control-sm"
                      :placeholder="field.placeholder"
                      required
                    ></textarea>
                    <select 
                      v-else-if="field.field_type === 'select'"
                      v-model="customDetail[field.name]"
                      class="form-control form-control-sm"
                      required
                    >
                      <option value="">-- Select --</option>
                      <option v-for="opt in parseOptions(field.options)" :key="opt" :value="opt">
                        {{ opt }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <!-- Existing Details Template: Select Users -->
            <div v-if="selectedTemplate && selectedTemplate.template_type === 'existing_details'" class="mb-3">
              <label class="form-label">Users *</label>
              <multiselect ref="userSelect" v-model="selectedUsers" :options="users" label="name" track-by="id"
                placeholder="Select Users" :close-on-select="false" :multiple="true" :clear-on-select="false"
                :preserve-search="true" @select="() => { $refs.userSelect.search = '' }" />
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-primary" v-if="permissions.includes('document.download')" @click="downloadPdf">
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else>
    <UnauthorizedPage />
  </div>
</template>

<script>
import Vue from "vue";
import VueSweetalert2 from "vue-sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { mapActions } from "vuex";
import user from "@/ajax/user";
import UnauthorizedPage from "@/views/Layouts/UnauthorizedPage.vue";
import CustomPagination from "../../components/CustomPagination.vue";
import Multiselect from "vue-multiselect";
import * as bootstrap from "bootstrap";
import DocumentTable from "./DocumentTable.vue";
import AddDocument from "./AddDocument.vue";
import DocumentFieldsManager from "./DocumentFieldsManager.vue";
Vue.use(VueSweetalert2);

export default {
  name: "Documents",

  components: {
    UnauthorizedPage,
    CustomPagination,
    Multiselect,
    DocumentTable,
    AddDocument,
    DocumentFieldsManager,
  },

  data() {
    return {
      rows: [],
      totalRecords: 0,

      // ===== NEW =====
      downloadModal: null,
      selectedDocument: null,
      selectedTemplate: null,
      templates: [],
      templateFields: [],

      users: [],
      selectedUsers: [],
      customDetail: {},

      serverParams: {
        filters: { status: "" },
        sort: [{ field: "", type: "asc" }],
        page: 1,
        perpage: 100,
      },

      perpage_list: [10, 20, 30, 40, 50, "All"],
      permissions: window.localStorage.getItem("permissions"),

      columns: [
        {
          label: "Name",
          field: "name",
          sortable: true,
          thClass: "custom-th",
          tdClass: "custom-td",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "disable-sorting custom-th",
          tdClass: "custom-td text-nowrap",
          width: "15%",
        },
      ],
    };
  },

  computed: {
    documentNames() {
      return this.rows.map((row) => row.name);
    },
  },
  watch: {
    $route(to) {
      if (to.query.refresh === "true") {
        this.getAllDocuments();
        this.$router.replace({ query: {} });
      }
    }
  },
  methods: {
    ...mapActions("common", ["setCurrentPage"]),
    async getUsers() {
      try {
        const res = await user.getUsers();
        this.users = res.data.data || [];
      } catch (e) {
        this.users = [];
      }
    },
    goToDocument(row) {
      this.$router.push({
        name: "DocumentEdit",
        params: { id: row.id },
      });
    },

    openModal() {
      this.$refs.docModal.open();
    },

    openFieldsManager() {
      this.$refs.fieldsModal.openModal();
    },

    openDownloadModal(row) {
      this.selectedDocument = row;
      this.selectedUsers = [];
      this.selectedTemplate = null;

      user
        .getTemplates(row.id)
        .then((res) => {
          this.templates = res.data.data || [];
        })
        .catch((err) => {
          this.templates = [];
          const msg =
            (err.response && err.response.data && err.response.data.detail) ||
            "Unable to load templates for this document.";
          this.$swal("Error", msg, "error");
        });

      this.downloadModal.show();
    },

    closeDownloadModal() {
      this.downloadModal.hide();
    },

    async onTemplateChange() {
      this.templateFields = [];
      this.selectedUsers = [];
      this.customDetail = {};

      if (!this.selectedTemplate) return;

      // Load template fields if it's a custom details template
      if (this.selectedTemplate.template_type === 'custom_details') {
        try {
          const res = await user.getTemplateFields(this.selectedTemplate.id);
          this.templateFields = res.data.data || [];
          // Initialize empty candidate with all fields
          this.resetCandidate();
        } catch (err) {
          console.error("Error loading template fields:", err);
        }
      }
    },

    resetCandidate() {
      const newCandidate = {};
      this.templateFields.forEach(field => {
        newCandidate[field.name] = '';
      });
      this.customDetail = newCandidate;
    },

    parseOptions(optionsString) {
      if (!optionsString) return [];
      try {
        return JSON.parse(optionsString);
      } catch {
        return optionsString.split(',').map(o => o.trim());
      }
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    toPascalCase(str) {
      if (!str) return '';
      return str
        .toLowerCase()
        .split(/[\s_-]+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    formatAmountInIndianRupees(value) {
      if (!value) return '₹0.00';
      const num = parseFloat(value);
      if (isNaN(num)) return '₹0.00';
      return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    validateAmountInput(fieldName) {
      // Remove any non-numeric characters except decimal point
      let value = this.customDetail[fieldName];
      if (value) {
        // Allow only digits and single decimal point
        value = value.replace(/[^0-9.]/g, '');
        // Ensure only one decimal point
        const parts = value.split('.');
        if (parts.length > 2) {
          value = parts[0] + '.' + parts.slice(1).join('');
        }
        this.customDetail[fieldName] = value;
      }
    },

    formatAmountInput(fieldName) {
      // Keep the numeric value in the model, format only for display
      const value = this.customDetail[fieldName];
      if (value && !isNaN(value)) {
        // Store as number, display will handle formatting in PDF
        this.customDetail[fieldName] = parseFloat(value);
      }
    },

    async downloadPdf() {
      // Validation
      if (!this.selectedTemplate) {
        this.$swal("Error", "Please select a template", "error");
        return;
      }

      // Validate based on template type
      if (this.selectedTemplate.template_type === 'existing_details' && !this.selectedUsers.length) {
        this.$swal("Error", "Please select at least one user", "error");
        return;
      }

      if (this.selectedTemplate.template_type === 'custom_details') {
        // Validate all fields are filled
        const missingFields = this.templateFields.filter(
          field => !this.customDetail[field.name] || this.customDetail[field.name].toString().trim() === ''
        );
        if (missingFields.length > 0) {
          const fieldNames = missingFields.map(f => f.label).join(', ');
          this.$swal("Error", `Please fill in all required fields: ${fieldNames}`, "error");
          return;
        }

        const invalidEmails = this.templateFields.filter(
          field => field.field_type === 'email' && this.customDetail[field.name] && !this.isValidEmail(this.customDetail[field.name])
        );
        if (invalidEmails.length > 0) {
          const fieldNames = invalidEmails.map(f => f.label).join(', ');
          this.$swal("Error", `Please enter valid email addresses for: ${fieldNames}`, "error");
          return;
        }
      }

      const payload = {
        template_id: this.selectedTemplate.id,
        user_ids: this.selectedUsers.map(u => u.id),
      };

      if (this.selectedTemplate.template_type === "custom_details") {
        payload.custom_details = [this.customDetail];
      }
      try {
        const res = await user.downloadDocumentPdf(payload);

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "document.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        this.downloadModal.hide();
      } catch (err) {
        let msg = "Failed to download document.";

        if (err.response && err.response.data) {
          try {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(reader.result);
                msg = json.detail || msg;
              } catch {
                // keep default
              }
              this.$swal("Error", msg, "error");
            };
            reader.readAsText(err.response.data);
          } catch {
            this.$swal("Error", msg, "error");
          }
        } else {
          this.$swal("Error", msg, "error");
        }
      }
    },


    async saveDocument(name) {
      if (!name.trim()) {
        this.$swal("Warning", "Document name required", "warning");
        return;
      }

      try {
        await user.createDocument({ name: name });
        this.$refs.docModal.close();
        await this.getAllDocuments();
        this.$swal("Success", "Document added successfully", "success");

      } catch (e) {
        this.$swal("Error", "Failed to create document", "error");
      }
    },
    async getAllDocuments() {
      const result = await user.getAllDocuments();
      this.rows = result.data.data;
      this.totalRecords = result.data.data.length;
    },

    deleteDocument(id) {
      this.$swal({
        title: "Delete",
        text: "Are you sure you want to delete this document?",
        icon: "question",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#32a852",
        cancelButtonColor: "#f00",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value || result.isConfirmed) {
          user
            .deleteDocument(id)
            .then((res) => {
              const msg =
                res.data && res.data.message
                  ? res.data.message
                  : "Document deleted successfully";
              this.getAllDocuments();
              this.$swal("Success", msg, "success");
            })
            .catch((err) => {
              let message =
                "Unable to delete document. It may have templates linked.";
              if (err.response && err.response.data) {
                message =
                  err.response.data.detail ||
                  err.response.data.message ||
                  message;
              }
              this.$swal("Error", message, "error");
            });
        }
      });
    }


  },

  mounted() {
    this.getAllDocuments();
    this.downloadModal = new bootstrap.Modal(this.$refs.downloadModal);
    this.getUsers();
    if (this.$route.query.refresh === "true") {
      this.getAllDocuments();
      this.$router.replace({ query: {} });
    }
  },
};
</script>
